import { useMemo, useState } from 'react';
import { useProjectInstagramCampaigns } from '../hooks/useProjectInstagramCampaigns';
import useInvalidateProjectCampaigns from '../hooks/useInvalidateProjectCampaigns';
import { Button, assets } from '@round/whitelabel-ui';
import CreateInstagramCampaignModal from './CreateInstagramCampaignModal/CreateInstagramCampaignModal';
import { creatorbase } from '@round/api';
import { getReceivedCampaignCurrencyId, isReceivedCampaign } from 'Modules/Plans/Campaigns/helpers';
import UpdateInstagramCampaignModal from './UpdateInstagramCampaignModal/UpdateInstagramCampaignModal';
import EditReceivedCampaignModal from '../components/EditReceivedCampaignModal/EditReceivedCampaignModal';
import ProjectCampaignsTable, {
    AddCreatorsTarget,
    CampaignTableRow,
} from '../components/ProjectCampaignsTable/ProjectCampaignsTable';
import styles from './InstagramCampaigns.module.css';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import useProjectDetails from '../useProjectDetails';
import { useProjectCampaignsStats } from '../hooks/useProjectCampaignsStats';
import { buildInstagramAudioUrl, mapStringListToArray, showNotification } from 'helpers';
import { ExpandedState } from '@tanstack/react-table';
import InstagramPosts from '../components/PostsTables/InstagramPostsTable/InstagramPosts';
import useRefreshProjectCampaignStats from '../hooks/useRefreshProjectCampaignStats';
import useCreateProjectDetailsPost from '../hooks/posts/useCreateProjectDetailsPost';
import { useInvalidatePosts } from '@round/creatorbase-helpers/api-hooks';
import { getProjectDetailsPostsParams } from '../hooks/posts/getProjectDetailsPostsParams';
import CreatePostModal from '../Posts/CreatePostModal/CreatePostModal';
import BulkCreatePostModal from '../Posts/BulkCreatePostModal/BulkCreatePostModal';
import useUrlState from 'Hooks/useUrlState';
import { isNumber, isNotNullOrUndefined } from 'utility/utility';
import DeleteCampaignModal from '../components/DeleteCampaignModal/DeleteCampaignModal';
import { useReorderProjectCampaigns } from '../hooks/useReorderProjectCampaigns';
import InstagramAudioModal from 'Modules/Plans/Audios/AudioModal/InstagramAudioModal/InstagramAudioModal';
import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import CampaignBriefModal from '../components/CampaignBriefModal/CampaignBriefModal';
import { formatBriefDataToWhatsAppMarkdown } from '../helpers/copyToClipboard.helpers';
import copy from 'copy-to-clipboard';
import useProjectId from '../hooks/useProjectId';
import PurchaseOrderAllocationModal from 'Modules/Plans/Finance/PurchaseOrders/PurchaseOrderAllocationModal/PurchaseOrderAllocationModal';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable';
import AddToCampaignModal from '../components/AddCreatorsToCampaignModal/AddToCampaignModal';
import AddFromPoolModal from '../components/AddCreatorsFromPoolModal/AddFromPoolModal';
import useCampaignPurchaseOrders from '../hooks/useCampaignPurchaseOrders';
import useCopyInstagramReportToClipboard from '../hooks/copyToClipboard/useCopyInstagramReportToClipboard';
import useCopyInstagramProposalToClipboard from '../hooks/copyToClipboard/useCopyInstagramProposalToClipboard';
import useCopyInstagramAudienceProposalToClipboard from '../hooks/copyToClipboard/useCopyInstagramAudienceProposalToClipboard';
import useCopyInstagramPostUrlsToClipboard from '../hooks/copyToClipboard/useCopyInstagramPostUrlsToClipboard';

const { Plus: PlusIcon } = assets;

type UrlState = Partial<{
    expanded: string;
    backupExpanded: string;
    audioId: number;
}>;

const InstagramCampaigns = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const { currencies } = useNonNullContext(OptionsContext);

    const { project, users, team, brand } = useProjectDetails();
    const projectId = useProjectId();

    const {
        data,
        isError: hasCampaignsError,
        isInitialized: areCampaignsInitialized,
        audios,
        audioTimeSeries,
    } = useProjectInstagramCampaigns();
    const instagramCampaigns = data?.results;
    const invalidateProjectCampaigns = useInvalidateProjectCampaigns();

    const { data: campaignsStats, loadingCampaignIds: campaignStatsLoading } = useProjectCampaignsStats(
        instagramCampaigns?.map((c) => c.id) ?? []
    );

    const campaignIds = instagramCampaigns?.map((c) => c.id) ?? [];
    const campaignPurchaseOrders = useCampaignPurchaseOrders({ campaignIds: campaignIds });

    const reorderCampaigns = useReorderProjectCampaigns('instagram');

    const tableRows: CampaignTableRow[] = useMemo(() => {
        return (
            instagramCampaigns?.map((campaign) => {
                const teamMembers = users?.results.filter((user) => campaign.team_members.includes(user.id)) ?? [];

                const stats = campaignsStats[campaign.id]?.data ?? null;
                const currencyId = isReceivedCampaign(campaign)
                    ? getReceivedCampaignCurrencyId(stats?.cpm)
                    : campaign.currency_id;
                const currency = currencies.find((c) => c.id === currencyId);
                const audioId = campaign.instagram_details?.audio_id;
                const audioUrl =
                    audioId && audios.data[audioId]?.instagram_id
                        ? buildInstagramAudioUrl(audios.data[audioId].instagram_id)
                        : undefined;
                const audioTimeSeriesRow: CampaignTableRow['audioTimeSeries'] = (() => {
                    if (!audioId) return null;
                    const tsData = audioTimeSeries.data[audioId] ?? null;
                    if (audioTimeSeries.isError) {
                        return { status: 'error', data: tsData, error: 'Could not load time series' };
                    }
                    if (audioTimeSeries.isLoading) {
                        return { status: 'loading', data: tsData, error: null };
                    }
                    return tsData
                        ? { status: 'success', data: tsData, error: null }
                        : { status: 'idle', data: null, error: null };
                })();
                const purchaseOrders =
                    campaignPurchaseOrders.data
                        ?.filter((i) => i.campaignId === campaign.id)
                        .map((i) => i.purchaseOrder)
                        .filter(isNotNullOrUndefined) ?? [];

                return {
                    ...campaign,
                    teamMembers,
                    currency,
                    stats,
                    audioUrl,
                    audioTimeSeries: audioTimeSeriesRow,
                    purchaseOrders,
                };
            }) ?? []
        );
    }, [
        instagramCampaigns,
        currencies,
        users,
        campaignsStats,
        audios.data,
        audioTimeSeries.data,
        audioTimeSeries.isLoading,
        audioTimeSeries.isError,
        campaignPurchaseOrders.data,
    ]);

    const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false);
    const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);
    const [isEditBriefModalOpen, setIsEditBriefModalOpen] = useState(false);
    const [campaignToEdit, setCampaignToEdit] = useState<CampaignTableRow | null>(null);
    const [receivedCampaignToEdit, setReceivedCampaignToEdit] = useState<CampaignTableRow | null>(null);
    const [isConfirmDeleteCampaignModalOpen, setIsConfirmDeleteCampaignModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState<CampaignTableRow | null>(null);

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const refreshAllStats = useRefreshProjectCampaignStats();
    const { createPost } = useCreateProjectDetailsPost();
    const invalidatePosts = useInvalidatePosts();

    const { clients } = useCurrentUserClients();
    const isPaymentEnabled = !!clients.find((c) => c.id === team?.client_id)?.is_payment_enabled;

    const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
    const [isBulkCreatePostModalOpen, setIsBulkCreatePostModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<CampaignTableRow | null>(null);

    const onCreatePost = (campaign: CampaignTableRow) => {
        setIsCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const onBulkCreatePost = (campaign: CampaignTableRow) => {
        setIsBulkCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const copyBriefToClipboard = (campaign: CampaignTableRow) => {
        const audioId = campaign.instagram_details?.audio_id;
        const audio = audioId ? audios.data[audioId] ?? null : null;

        const formattedBriefData = formatBriefDataToWhatsAppMarkdown({
            audioUrl: audio ? buildInstagramAudioUrl(audio.instagram_id) : undefined,
            brandName: brand?.name,
            projectName: project?.name,
            campaignName: campaign.name,
            brief: campaign.brief,
            turnaround: campaign.brief_turnaround,
        });

        const success = copy(formattedBriefData, { format: 'text/plain' });

        if (success) {
            showNotification('Brief copied!', 'info');
        } else {
            showNotification('Could not copy brief', 'error');
        }
    };

    const copyReportTableToClipboard = useCopyInstagramReportToClipboard();

    const expandedBackupCampaigns = mapStringListToArray(urlState.backupExpanded).map(Number);
    const toggleExpandedBackupCampaigns = (campaignId: number) => {
        if (expandedBackupCampaigns.includes(campaignId)) {
            setUrlState({ backupExpanded: expandedBackupCampaigns.filter((id) => id !== campaignId).toString() });
            return;
        }

        setUrlState({ backupExpanded: expandedBackupCampaigns.concat(campaignId).toString() });
    };

    const copyProposalTableToClipboard = useCopyInstagramProposalToClipboard();
    const copyAudienceProposalTableToClipboard = useCopyInstagramAudienceProposalToClipboard();

    const copyPostUrlsToClipboard = useCopyInstagramPostUrlsToClipboard();

    const [allocationCampaignId, setAllocationCampaignId] = useState<number | null>(null);
    const [addCreatorsTarget, setAddCreatorsTarget] = useState<AddCreatorsTarget | null>(null);

    return (
        <div className={styles.container}>
            <div className={styles.actions}>
                <PostUrlClipboardButton onClick={() => copyPostUrlsToClipboard(instagramCampaigns ?? [])} />
                <Button
                    appearance="primary"
                    onClick={() => setIsCreateCampaignModalOpen(true)}
                    iconLeft={<PlusIcon className={styles.plusIcon} />}
                >
                    Campaign
                </Button>
            </div>

            <ProjectCampaignsTable
                data={tableRows}
                onAddCreatorsToCampaign={(campaign, source) => {
                    setAddCreatorsTarget({ campaignId: campaign.id, source });
                }}
                onEdit={(c) => {
                    if (isReceivedCampaign(c)) {
                        setReceivedCampaignToEdit(c);
                        return;
                    }

                    setCampaignToEdit(c);
                    setIsEditCampaignModalOpen(true);
                }}
                onEditBrief={(c) => {
                    setCampaignToEdit(c);
                    setIsEditBriefModalOpen(true);
                }}
                onCopyBrief={copyBriefToClipboard}
                onCopyReport={copyReportTableToClipboard}
                onCopyProposal={copyProposalTableToClipboard}
                onCopyAudienceProposal={copyAudienceProposalTableToClipboard}
                onCreatePost={onCreatePost}
                onBulkCreatePost={onBulkCreatePost}
                onCreateCampaign={() => setIsCreateCampaignModalOpen(true)}
                onDeleteCampaign={(campaign) => {
                    setIsConfirmDeleteCampaignModalOpen(true);
                    setCampaignToDelete(campaign);
                }}
                onAllocate={(row) => setAllocationCampaignId(row.id)}
                hasError={hasCampaignsError}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => (
                    <InstagramPosts
                        campaign={row.original}
                        isPaymentEnabled={isPaymentEnabled}
                        isBackupExpanded={expandedBackupCampaigns.includes(row.original.id)}
                        onBackupExpandToggle={() => toggleExpandedBackupCampaigns(row.original.id)}
                        onAddCreatorsToCampaign={(campaign) =>
                            setAddCreatorsTarget({ campaignId: campaign.id, source: 'explore' })
                        }
                        onCreatePost={() => onCreatePost(row.original)}
                    />
                )}
                isLoading={!areCampaignsInitialized}
                statsLoading={campaignStatsLoading}
                arePurchaseOrdersLoading={campaignPurchaseOrders.isLoading}
                onDragEnd={reorderCampaigns}
                onAudioGrowthCellClick={(row) => {
                    if (!row.instagram_details?.audio_id) {
                        return;
                    }

                    setUrlState({ audioId: row.instagram_details.audio_id });
                }}
            />

            <CreateInstagramCampaignModal
                isOpen={isCreateCampaignModalOpen}
                closeModal={() => setIsCreateCampaignModalOpen(false)}
            />

            <EditReceivedCampaignModal
                isOpen={!!receivedCampaignToEdit}
                onClose={() => setReceivedCampaignToEdit(null)}
                campaign={receivedCampaignToEdit}
            />

            <UpdateInstagramCampaignModal
                isOpen={isEditCampaignModalOpen}
                closeModal={() => {
                    setIsEditCampaignModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit as creatorbase.InstagramCampaign | null}
            />

            <DeleteCampaignModal
                isOpen={isConfirmDeleteCampaignModalOpen}
                campaign={campaignToDelete}
                onClose={() => {
                    setIsConfirmDeleteCampaignModalOpen(false);
                    setCampaignToDelete(null);
                }}
            />

            <CampaignBriefModal
                isOpen={isEditBriefModalOpen}
                closeModal={() => {
                    setIsEditBriefModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit}
            />

            <CreatePostModal
                isOpen={isCreatePostModalOpen}
                closeModal={() => {
                    setIsCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={instagramCampaigns?.find((c) => c.id === selectedCampaign?.id)}
                createPost={createPost}
            />

            <BulkCreatePostModal
                isOpen={isBulkCreatePostModalOpen}
                closeModal={() => {
                    setIsBulkCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={instagramCampaigns?.find((c) => c.id === selectedCampaign?.id)}
            />

            {urlState.audioId && (
                <InstagramAudioModal
                    isOpen={Boolean(urlState.audioId)}
                    onClose={() => setUrlState({ audioId: undefined })}
                    audioId={Number(urlState.audioId)}
                />
            )}

            <PurchaseOrderAllocationModal
                campaignId={allocationCampaignId}
                isOpen={isNumber(allocationCampaignId)}
                onClose={(hasUpdated) => {
                    if (hasUpdated && allocationCampaignId) {
                        invalidateProjectCampaigns('instagram');
                    }

                    setAllocationCampaignId(null);
                }}
            />

            <AddToCampaignModal
                key={`add-to-campaign-${addCreatorsTarget?.campaignId}`}
                isOpen={addCreatorsTarget?.source === 'explore'}
                onClose={(hasUpdated) => {
                    if (hasUpdated && addCreatorsTarget) {
                        invalidatePosts(getProjectDetailsPostsParams(addCreatorsTarget.campaignId));
                        refreshAllStats(addCreatorsTarget.campaignId, 'instagram');
                    }
                    setAddCreatorsTarget(null);
                }}
                campaign={instagramCampaigns?.find(
                    (c): c is creatorbase.InstagramCampaign =>
                        c.id === addCreatorsTarget?.campaignId && c.platform === 'instagram'
                )}
                projectId={projectId}
            />

            <AddFromPoolModal
                key={`add-from-pool-${addCreatorsTarget?.campaignId}`}
                isOpen={addCreatorsTarget?.source === 'pool'}
                onClose={(hasUpdated) => {
                    if (hasUpdated && addCreatorsTarget) {
                        invalidatePosts(getProjectDetailsPostsParams(addCreatorsTarget.campaignId));
                        refreshAllStats(addCreatorsTarget.campaignId, 'instagram');
                    }
                    setAddCreatorsTarget(null);
                }}
                campaign={instagramCampaigns?.find(
                    (c): c is creatorbase.InstagramCampaign =>
                        c.id === addCreatorsTarget?.campaignId && c.platform === 'instagram'
                )}
            />
        </div>
    );
};

export default InstagramCampaigns;
